/**
 * AdminReferralCrm — Referral Source CRM (iter 170 modular rewrite).
 *
 * The BD-team home page. Every referring facility gets a full profile:
 * contacts, meeting notes, follow-up tasks, and live volume/conversion
 * stats pulled from the intake_referrals aggregate. Auto-populates as
 * intakes are accepted/rejected.
 *
 * Route: /admin/crm (admin + provider org roles)
 * Testids: admin-crm-page, crm-row-<slug>, crm-drawer, crm-add-contact,
 *   crm-add-note, crm-add-task, crm-task-toggle-<id>, crm-new-facility-btn
 *
 * Sub-components live in /components/crm/ so this file stays focused on
 * the list view + KPI strip + drawer/modal wiring.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, Loader2, Search, Plus, Users, CheckSquare,
  ChevronRight, Shield, ShieldCheck, Inbox, Percent,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';
import { API, slugify, fmtPct, fmtHours } from '../components/crm/formatters';
import { Kpi } from '../components/crm/uiKit';
import { FacilityDrawer } from '../components/crm/FacilityDrawer';
import { NewFacilityModal } from '../components/crm/NewFacilityModal';

const ADMIN_NAV = [
  { icon: Shield, label: 'Compliance', href: '/admin/compliance' },
  { icon: ShieldCheck, label: 'AI Validation', href: '/admin/ai-validation' },
  { icon: Inbox, label: 'Intake Inbox', href: '/admin/intake' },
  { icon: Building2, label: 'Referral Sources', href: '/admin/referral-sources' },
  { icon: Users, label: 'CRM', href: '/admin/crm', active: true },
  { icon: ShieldCheck, label: 'HIE Console', href: '/admin/hie' },
];

export default function AdminReferralCrm() {
  const { getAuthHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/crm/facilities`, {
        ...getAuthHeader(),
        params: { include_all_orgs: true, with_stats: true, q: q || undefined },
      });
      setRows(data.rows || []);
    } catch (e) {
      toast.error('Could not load CRM');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, q]);

  useEffect(() => { load(); }, [load]);

  const kpis = useMemo(() => {
    const totals = rows.reduce((acc, r) => {
      const s = r.volume_snapshot || {};
      acc.vol += (s.total_referrals || 0);
      acc.acc += (s.accepted || 0);
      acc.rej += (s.rejected || 0);
      acc.tasksOpen += (r.tasks || []).filter((t) => t.status === 'open').length;
      return acc;
    }, { vol: 0, acc: 0, rej: 0, tasksOpen: 0 });
    const decided = totals.acc + totals.rej;
    totals.conversion = decided ? totals.acc / decided : null;
    return totals;
  }, [rows]);

  return (
    <DashboardShell navItems={ADMIN_NAV}>
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="admin-crm-page">
        <header className="flex flex-wrap items-end gap-4 justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-100 tracking-tight">
              Referral CRM
            </h1>
            <p className="text-sm text-slate-400 mt-1 max-w-xl">
              Every referring facility as a full profile — contacts, meeting notes, follow-up tasks, live volume + conversion. Auto-populated by intake accept/reject.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
              <input
                type="text"
                placeholder="Search facilities…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-7 pr-3 py-1.5 bg-slate-900 border border-slate-800 rounded-md text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cn-primary/60 w-56"
                data-testid="crm-search"
              />
            </div>
            <Button onClick={() => setShowCreate(true)} data-testid="crm-new-facility-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> New facility
            </Button>
          </div>
        </header>

        <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Facilities" value={rows.length} icon={Building2} testId="crm-kpi-facilities" />
          <Kpi label="Total referrals" value={kpis.vol} icon={Users} sub={`${kpis.acc} accepted · ${kpis.rej} rejected`} testId="crm-kpi-referrals" />
          <Kpi label="Overall conversion" value={fmtPct(kpis.conversion)} icon={Percent} testId="crm-kpi-conversion" />
          <Kpi label="Open follow-ups" value={kpis.tasksOpen} icon={CheckSquare} sub="tasks across all facilities" testId="crm-kpi-tasks" />
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
          <div className="p-4 border-b border-slate-800 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-slate-500" /> Facilities ({rows.length})
            </h2>
          </div>
          {loading ? (
            <div className="p-10 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="crm-empty">
              No facilities yet. Uploaded packets that get accepted automatically populate this list.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-800">
                  <th className="px-4 py-2 font-medium">Facility</th>
                  <th className="px-4 py-2 font-medium text-right">Volume</th>
                  <th className="px-4 py-2 font-medium text-right">Conversion</th>
                  <th className="px-4 py-2 font-medium text-right">Time to admit</th>
                  <th className="px-4 py-2 font-medium">Contacts</th>
                  <th className="px-4 py-2 font-medium">Open tasks</th>
                  <th className="px-4 py-2 font-medium text-right"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const s = r.volume_snapshot || {};
                  const openTasks = (r.tasks || []).filter((t) => t.status === 'open').length;
                  return (
                    <tr
                      key={r.id}
                      className="border-b border-slate-800/60 hover:bg-slate-800/30 cursor-pointer transition-colors"
                      onClick={() => setOpenId(r.id)}
                      data-testid={`crm-row-${slugify(r.name)}`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-100">{r.name}</div>
                        {r.address && <div className="text-xs text-slate-500 truncate max-w-[300px]">{r.address}</div>}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">{s.total_referrals ?? 0}</td>
                      <td className="px-4 py-3 text-right">{fmtPct(s.conversion_rate)}</td>
                      <td className="px-4 py-3 text-right">{fmtHours(s.avg_time_to_admit_hours)}</td>
                      <td className="px-4 py-3 text-xs text-slate-400">{(r.contacts || []).length}</td>
                      <td className="px-4 py-3">
                        {openTasks > 0 ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-xs font-medium border border-amber-500/30">
                            {openTasks} open
                          </span>
                        ) : (
                          <span className="text-xs text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <ChevronRight className="h-4 w-4 text-slate-600 inline" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {openId && (
        <FacilityDrawer
          facilityId={openId}
          onClose={() => setOpenId(null)}
          onChanged={load}
          getAuthHeader={getAuthHeader}
        />
      )}
      {showCreate && (
        <NewFacilityModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => { setShowCreate(false); load(); setOpenId(id); }}
          getAuthHeader={getAuthHeader}
        />
      )}
    </DashboardShell>
  );
}
